import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close as CloseIcon, DeleteOutline as DeleteIcon } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import { useSelector } from "react-redux";
import useApiCall from "../../hooks/useApiCall";
import { INDIGO, SLATE } from "../../constants/Colors";
import NewTestRunDialog from "./NewTestRunDialog";
import ResultSheetDialog from "./ResultSheetDialog";

const POLL_INTERVAL_MS = 5000;
const IN_FLIGHT_STATUSES = ["queued", "running"];

const ALL_MARKETS = "__all__";

const COL_HEADER_SX = {
  fontSize: "0.6875rem",
  fontWeight: 600,
  color: SLATE[500],
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  borderBottom: `1px solid ${SLATE[200]}`,
};

const CELL_SX = {
  fontSize: "0.8125rem",
  color: SLATE[800],
  borderBottom: `1px solid ${SLATE[100]}`,
};

const STATUS_COLORS = {
  queued: "default",
  running: "info",
  completed: "success",
  failed: "error",
};

function formatTimestamp(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleString();
}

/**
 * Lists past LLM vs legacy comparison runs and links each result sheet against the
 * input file it came from. Runs are asynchronous, so the list refreshes itself while
 * anything is queued or running and stops as soon as nothing is -- a finished list must
 * not keep hitting the endpoint.
 */
export default function TestRunsDialog({ open, onClose, tokens }) {
  const apps = useSelector((state) => state?.apps.list || []);
  const { makeRequest } = useApiCall();

  const [runs, setRuns] = useState([]);
  const [market, setMarket] = useState(ALL_MARKETS);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);
  const [newTestOpen, setNewTestOpen] = useState(false);
  const [sheetRun, setSheetRun] = useState(null);

  // Read inside the polling effect so a refetch does not restart the interval.
  const marketRef = useRef(market);
  marketRef.current = market;

  const marketItems = useMemo(
    () => apps.map((app) => ({ value: app.market, label: app.market })),
    [apps]
  );

  const fetchRuns = useCallback(
    async ({ showSpinner = false } = {}) => {
      const selected = marketRef.current;
      const url =
        selected === ALL_MARKETS
          ? "/v1/intent_model_test_runs"
          : `/v1/intent_model_test_runs?market=${encodeURIComponent(selected)}`;

      if (showSpinner) setLoading(true);
      try {
        const response = await makeRequest(url, { method: "GET", authTokens: tokens });
        setRuns(response?.test_runs || []);
        setError(null);
      } catch (e) {
        setError(e?.message || "Failed to load intent model test runs.");
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [makeRequest, tokens]
  );

  // Every open starts from scratch. Dropping the rows and any error first means a
  // reopen never flashes the previous session's list, which by then may be stale --
  // those runs can have finished, failed, or been added to since it was closed. The
  // same reset covers a market change, so rows for the old market do not linger.
  useEffect(() => {
    if (!open) return undefined;

    setRuns([]);
    setError(null);
    fetchRuns({ showSpinner: true });
    return undefined;
  }, [open, market]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    if (refreshing) return;

    setRefreshing(true);
    try {
      await fetchRuns();
    } finally {
      setRefreshing(false);
    }
  };

  // Removes the run's input file, status and result sheet from S3. Nothing recovers
  // them, so it asks first.
  const handleDelete = async (run) => {
    const confirmed = window.confirm(
      `Delete test run ${run.run_id}?\n\n` +
        "This permanently removes its input file, status and result sheet from S3."
    );
    if (!confirmed) return;

    setDeletingId(run.run_id);
    setError(null);
    try {
      await makeRequest(`/v1/intent_model_test_runs/${encodeURIComponent(run.run_id)}`, {
        method: "DELETE",
        authTokens: tokens,
      });
      await fetchRuns();
    } catch (e) {
      setError(e?.message || "Failed to delete the test run.");
    } finally {
      setDeletingId(null);
    }
  };

  const hasRunInFlight = runs.some((run) => IN_FLIGHT_STATUSES.includes(run.status));

  useEffect(() => {
    if (!open || !hasRunInFlight) return undefined;

    const timer = setInterval(() => fetchRuns(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [open, hasRunInFlight]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    setNewTestOpen(false);
    setSheetRun(null);
    onClose();
  };

  const progressFor = (run) => {
    if (run.status === "completed") return `${run.match_count ?? 0} / ${run.sentence_count ?? 0}`;
    if (run.status === "running") return `${run.processed_count ?? 0} / ${run.sentence_count ?? 0}`;
    return "—";
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: "92vh", maxHeight: "92vh", width: "96vw", maxWidth: "1600px" } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900] }}>
                Test Models
              </Typography>
              <Typography sx={{ fontSize: "0.8125rem", color: SLATE[500] }}>
                Compare the LLM intent model against the legacy model.
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <TextField
                select
                size="small"
                id="test-runs-market"
                label="Market"
                value={market}
                onChange={(e) => setMarket(e.target.value)}
                sx={{ minWidth: 180, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem" } }}
              >
                <MenuItem value={ALL_MARKETS}>All markets</MenuItem>
                {marketItems.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
              <Tooltip title="Refresh">
                <span>
                  <IconButton
                    onClick={handleRefresh}
                    disabled={refreshing}
                    size="small"
                    aria-label="Refresh"
                  >
                    {refreshing ? (
                      <CircularProgress size={16} />
                    ) : (
                      <RefreshIcon sx={{ fontSize: "1.25rem", color: SLATE[600] }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: "1rem" }} />}
                onClick={() => setNewTestOpen(true)}
                sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none", whiteSpace: "nowrap" }}
              >
                New Test
              </Button>
              <IconButton onClick={handleClose} size="small">
                <CloseIcon sx={{ fontSize: "1.1rem", color: SLATE[500] }} />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ minHeight: 240 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, fontSize: "0.8125rem" }}>
              {error}
            </Alert>
          )}

          {loading && runs.length === 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress size={22} />
            </Box>
          )}

          {!loading && !error && runs.length === 0 && (
            <Box sx={{ textAlign: "center", py: 5 }}>
              <Typography sx={{ fontSize: "0.875rem", color: SLATE[600] }}>
                No test runs yet.
              </Typography>
              <Typography sx={{ fontSize: "0.8125rem", color: SLATE[500], mt: "4px" }}>
                Use New Test to compare a batch of sentences across both models.
              </Typography>
            </Box>
          )}

          {runs.length > 0 && (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={COL_HEADER_SX}>Market</TableCell>
                    <TableCell sx={COL_HEADER_SX}>Sentences</TableCell>
                    <TableCell sx={COL_HEADER_SX}>Matched</TableCell>
                    <TableCell sx={COL_HEADER_SX}>Status</TableCell>
                    <TableCell sx={COL_HEADER_SX}>Created</TableCell>
                    <TableCell sx={COL_HEADER_SX}>Input file</TableCell>
                    <TableCell sx={COL_HEADER_SX}>Result sheet</TableCell>
                    <TableCell sx={COL_HEADER_SX} align="center">Download</TableCell>
                    <TableCell sx={COL_HEADER_SX} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runs.map((run) => (
                    <TableRow key={run.run_id} hover>
                      <TableCell sx={CELL_SX}>{run.market}</TableCell>
                      <TableCell sx={CELL_SX}>{run.sentence_count}</TableCell>
                      <TableCell sx={CELL_SX}>{progressFor(run)}</TableCell>
                      <TableCell sx={CELL_SX}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Chip
                            label={run.status}
                            size="small"
                            color={STATUS_COLORS[run.status] || "default"}
                            sx={{ fontSize: "0.6875rem", height: 20 }}
                          />
                          {run.status === "failed" && run.error_message && (
                            <Tooltip title={run.error_message}>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  color: SLATE[500],
                                  maxWidth: 260,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {run.error_message}
                              </Typography>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={CELL_SX}>{formatTimestamp(run.created_at)}</TableCell>
                      <TableCell sx={CELL_SX}>
                        {run.input_url ? (
                          <Link
                            href={run.input_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontSize: "0.8125rem", color: INDIGO[600] }}
                          >
                            {run.input_file_name}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell sx={CELL_SX}>
                        {run.result_url ? (
                          <Link
                            component="button"
                            type="button"
                            onClick={() => setSheetRun(run)}
                            sx={{
                              fontSize: "0.8125rem",
                              color: INDIGO[600],
                              textAlign: "left",
                              cursor: "pointer",
                            }}
                          >
                            {run.result_file_name}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell sx={CELL_SX} align="center">
                        {run.result_url ? (
                          <Tooltip title="Download result sheet">
                            <IconButton
                              component="a"
                              href={run.result_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              size="small"
                              aria-label="Download result sheet"
                            >
                              <DownloadIcon sx={{ fontSize: "1.1rem", color: INDIGO[600] }} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell sx={CELL_SX} align="right">
                        <Tooltip title="Delete run">
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Delete run"
                              disabled={deletingId === run.run_id}
                              onClick={() => handleDelete(run)}
                            >
                              {deletingId === run.run_id ? (
                                <CircularProgress size={15} />
                              ) : (
                                <DeleteIcon sx={{ fontSize: "1.1rem", color: SLATE[500] }} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} sx={{ textTransform: "none", fontSize: "0.875rem", color: SLATE[600] }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <NewTestRunDialog
        open={newTestOpen}
        onClose={() => setNewTestOpen(false)}
        onCreated={() => fetchRuns()}
        tokens={tokens}
      />

      <ResultSheetDialog
        open={Boolean(sheetRun)}
        onClose={() => setSheetRun(null)}
        run={sheetRun}
        tokens={tokens}
      />
    </>
  );
}
